"use client";

import Modal from "@/components/core/Modal";
import { memo, ReactNode, useEffect, useState, useCallback } from "react";
import { AddressData } from "@/components/shared/Forms/CreateSalon/schema";
import { X, Loader2 } from "lucide-react";

interface AddressModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSave: (data: AddressData) => void;
  initialValues?: AddressData;
  isEditing: boolean;
}

const emptyValues: AddressData = {
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "",
  country: "Brasil",
  zip_code: "",
};

// --- Componente InputField Atualizado ---
const InputField = memo(
  ({
    label,
    name,
    value,
    onChange,
    onBlur,
    onClear,
    placeholder,
    isLoading,
    required, // Recebe a prop required
  }: {
    label: string;
    name: keyof AddressData;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onClear: (name: keyof AddressData) => void;
    placeholder: string;
    isLoading?: boolean;
    required?: boolean; // Define o tipo
  }) => (
    <div className="flex flex-col gap-1.5 w-full">
      {/* O asterisco agora está dentro do span, ao lado do texto, para garantir que apareça */}
      <span className="text-sm font-bold text-gray-800 ml-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>

      <div className="relative group">
        <input
          name={name}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          onBlur={onBlur}
          required={required}
          className={`
          w-full bg-white border border-gray-200 rounded-full py-3 px-4 text-sm
          placeholder:text-gray-400 outline-none transition-all
          focus:border-red-400 focus:ring-2 focus:ring-red-100
          ${isLoading ? "pr-10" : "pr-10"}
        `}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        )}
        {!isLoading && value && (
          <button
            type="button"
            onClick={() => onClear(name)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  ),
);

InputField.displayName = "InputField";

const AddressModal = memo(
  ({
    isOpen,
    onOpenChange,
    onSave,
    initialValues,
    isEditing,
  }: AddressModalProps): ReactNode => {
    const [form, setForm] = useState<AddressData>(emptyValues);
    const [loadingCep, setLoadingCep] = useState(false);

    useEffect(() => {
      if (isOpen) {
        setForm(isEditing && initialValues ? initialValues : emptyValues);
      }
    }, [isOpen, initialValues, isEditing]);

    const isValid = (val: string | undefined) => val && val.trim().length > 0;

    const isFormValid =
      isValid(form.zip_code) &&
      isValid(form.street) &&
      isValid(form.number) &&
      isValid(form.district) &&
      isValid(form.city) &&
      isValid(form.state);

    const fetchCep = async (cep: string) => {
      const cleanCep = cep.replace(/\D/g, "");
      if (cleanCep.length !== 8) return;

      setLoadingCep(true);
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setForm((prev) => ({
            ...prev,
            zip_code: cep,
            street: data.logradouro,
            district: data.bairro,
            city: data.localidade,
            state: data.uf,
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCep(false);
      }
    };

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
      },
      [],
    );

    const clearField = useCallback((name: keyof AddressData) => {
      setForm((prev) => ({ ...prev, [name]: "" }));
    }, []);

    function handleSubmit() {
      if (!isFormValid) return;
      onSave(form);
      onOpenChange(false);
    }

    const activeClasses =
      "bg-[#D64045] hover:bg-[#c2353a] text-white cursor-pointer";
    const inactiveClasses =
      "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50 pointer-events-none";

    const currentButtonClass = isFormValid ? activeClasses : inactiveClasses;

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        dialogTitle={isEditing ? "Editar endereço" : "Novo endereço"}
        dialogDescription={
          isEditing
            ? "Edite as informações do endereço"
            : "Preencha os dados do novo endereço"
        }
        className="flex flex-col w-full max-w-lg bg-white sm:rounded-3xl p-0 overflow-hidden"
      >
        <div className="p-6 flex flex-col gap-5">
          <InputField
            label="CEP"
            name="zip_code"
            value={form.zip_code}
            onChange={handleChange}
            onClear={clearField}
            placeholder="00000-000"
            isLoading={loadingCep}
            onBlur={(e) => fetchCep(e.target.value)}
            required={true}
          />
          <InputField
            label="Rua"
            name="street"
            value={form.street}
            onChange={handleChange}
            onClear={clearField}
            placeholder="Nome da rua"
            required // Adicionado
          />

          <div className="flex gap-4">
            <div className="w-1/3">
              <InputField
                label="Número"
                name="number"
                value={form.number}
                onChange={handleChange}
                onClear={clearField}
                placeholder="123"
                required // Adicionado
              />
            </div>
            <div className="w-2/3">
              <InputField
                label="Complemento"
                name="complement"
                value={form.complement || ""}
                onChange={handleChange}
                onClear={clearField}
                placeholder="Apto, Bloco..."
                // SEM required (Este é opcional)
              />
            </div>
          </div>

          <InputField
            label="Bairro"
            name="district"
            value={form.district}
            onChange={handleChange}
            onClear={clearField}
            placeholder="Bairro"
            required // Adicionado
          />

          <div className="flex gap-4">
            <div className="w-2/3">
              <InputField
                label="Cidade"
                name="city"
                value={form.city}
                onChange={handleChange}
                onClear={clearField}
                placeholder="Cidade"
                required // Adicionado
              />
            </div>
            <div className="w-1/3">
              <InputField
                label="UF"
                name="state"
                value={form.state}
                onChange={handleChange}
                onClear={clearField}
                placeholder="UF"
                required // Adicionado
              />
            </div>
          </div>

          <button
            onClick={handleSubmit}
            type="button"
            disabled={!isFormValid}
            className={`
              w-full font-bold text-sm rounded-full py-3.5 mt-4 transition-all shadow-sm
              ${currentButtonClass}
            `}
          >
            {isEditing ? "Salvar Alterações" : "Cadastrar Endereço"}
          </button>
        </div>
      </Modal>
    );
  },
);

AddressModal.displayName = "AddressModal";

export default AddressModal;
